import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Table,
  Accordion,
} from "react-bootstrap";
import {
  FaCheck,
  FaTimes,
  FaStar,
  FaRocket,
  FaShieldAlt,
  FaBolt,
  FaUsers,
  FaChartLine,
  FaCrown,
  FaGem,
  FaFire,
  FaTrophy,
  FaQuoteLeft,
  FaChevronDown,
  FaTag,
  FaDollarSign,
  FaInfoCircle,
} from "react-icons/fa";
import theme from "../../styles/theme/theme";
import { useNavigate } from "react-router-dom";

const PricingPage = () => {
  const [billingCycle, setBillingCycle] = useState("monthly");
  const navigate = useNavigate();

  // Helper function to convert hex to rgba
  const hexToRgba = (hex, alpha = 1) => {
    if (!hex) return `rgba(0,0,0,${alpha})`;
    let c = hex.replace("#", "");
    if (c.length === 3)
      c = c
        .split("")
        .map((x) => x + x)
        .join("");
    const bigint = parseInt(c, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const tp = theme?.palette || {};
  const primary = tp.primary?.main || "#F4C542";
  const secondary = tp.secondary?.main || "#FFF2A2";
  const tertiary = tp.tertiary?.main || "#AB992E";
  const success = tp.success?.main || "#74E5A3";
  const error = tp.error?.main || "#FFA27B";
  const textPrimary = tp.text?.primary || "#AB992E";
  const textSecondary = tp.text?.secondary || "#6E550C";
  const bgDefault = tp.background?.default || "#FFF8D0";
  const bgPaper = tp.background?.paper || "#ffffff";

  const pricingPlans = [
    {
      id: "free",
      name: "Market Explorer",
      price: "$0",
      period: "/30 days",
      tagline: "Free for 30 days from sign-up. 60 days for early bird members.",
      bestFor: "First-time sellers, occasional traders, testing the platform",
      features: [
        { text: "Up to 3 active listings at a time", included: true },
        { text: "Listings stay live for 30 days (auto-renew)", included: true },
        { text: "Browse all marketplace listings", included: true },
        { text: "Respond to buyer enquiries through platform", included: true },
        // { text: "Priority email support (24hr response)", included: true },
        // { text: "Featured placement", included: false },
        // { text: "Direct buyer contact details", included: false },
      ],
      commission: "Pay only when you sell",
      bonus:
        "Perfect if you're selling a one-off load or want to see how the market works.",
      cta: "Join as Early Bird Member",
      highlight: false,
      icon: <FaRocket />,
    },
    {
      id: "basic",
      name: "Active Trader",
      price: "$29.95",
      period: "/month",
      tagline: "Cancel anytime",
      bestFor: "Regular sellers, mobile collectors, small scrap yards",
      features: [
        { text: "Up to 20 active listings", included: true },
        { text: "Listings last 60 days", included: true },
        // { text: "Priority placement in search results", included: true },
        // { text: "Priority email support (24hr response)", included: true },
        { text: "Featured listings", included: true },
        { text: "Verified business badge", included: true },
        {
          text: "One mid-size copper load covers 3+ months",
          included: true,
          note: true,
        },
      ],
      commission: "Pay only when you sell",
      bonus:
        "Regular sellers unlock surprise deals. One mid-size copper load covers 3+ months of this plan.",
      cta: "Join as Early Bird Member",
      highlight: false,
      icon: <FaBolt />,
    },
    {
      id: "pro",
      name: "Business Operator",
      price: "$59.95",
      period: "/month",
      tagline:
        "~$43.95/month (27% off forever when you join now and off on yearly plan)",
      bestFor: "Scrap yards, recyclers, high-volume buyers & sellers",
      features: [
        { text: "Unlimited listings", included: true },
        { text: "3 featured listings per month ($15 value)", included: true },
        { text: "Verified business badge (builds trust)", included: true },
        {
          text: "SMS + email instant alerts (never miss a lead)",
          included: true,
        },
        // { text: "Priority support (24hr response)", included: true },
        // {
        //   text: "Request listing edits (subject to admin approval)",
        //   included: true,
        // },
        // { text: "Bulk upload tools", included: true },
        // { text: "Direct analytics dashboard", included: true },
      ],
      commission: "Pay only when you sell",
      bonus:
        "Regular sellers unlock surprise deals. Top sellers close 15-20 deals/month. That's just $3-4 per deal with Business.",
      cta: "Join as Early Bird Member",
      highlight: true,
      badge: "Most Popular",
      launchSpecial:
        "LAUNCH SPECIAL: Sign up now → Free until May 2026 → Then pay just $43.95/month (27% off forever)",
      icon: <FaTrophy />,
    },
    {
      id: "enterprise",
      name: "Enterprise",
      price: "$149.95",
      period: "/month",
      tagline: "For high-volume operators and market leaders",
      bestFor:
        "Regional operators, high-volume recyclers with 50+ tonnes/month",
      features: [
        { text: "Multi-user accounts (team access)", included: true },
        // {
        //   text: "Category sponsorships ('Sponsored by YourBrand')",
        //   included: true,
        // },
        { text: "Co-branded business profile page", included: true },
        { text: "Dedicated account manager", included: true },
        { text: "Marketplace Advertisement", included: true },
        // { text: "Homepage banner placement (Next phase)", included: true },
        // { text: "API access & integrations (Next phase)", included: true },
        // { text: "Beta features access (Next phase)", included: true },
      ],
      commission: "Pay only when you sell",
      bonus:
        "Regular sellers unlock surprise deals. Custom packages available for multi-location operators, industry associations, and strategic partners.",
      launchSpecial:
        "LAUNCH SPECIAL: $104.95/month (~$35/month) — 30% off forever when you join before April 10, 2026",
      cta: "Contact Sales",
      highlight: false,
      icon: <FaCrown />,
    },
  ];

  const addons = [
    {
      name: "Featured Listing Boost",
      price: "$5 per listing",
      description:
        "Appear above standard listings in search results for 7 days. Get seen first when buyers are searching.",
      example:
        'Example: "Copper wire, 2 tonnes" in Sydney gets seen before all standard listings',
      icon: <FaStar />,
    },
    {
      name: "Homepage Spotlight",
      price: "$19 for 30 days",
      description:
        "Your listing featured on ScrapTrade homepage. Perfect for high-value loads that need maximum visibility.",
      example: "Perfect for: industrial machinery, bulk steel, rare materials",
      icon: <FaFire />,
    },
    {
      name: "Verified Business Badge",
      price: "$39 for 3 months",
      description:
        "Show buyers you're legitimate with ID verification. Included free in Pro & Business plans.",
      example: "Available for Basic plan members — build trust instantly",
      icon: <FaShieldAlt />,
    },
    {
      name: "Transport Partner Network",
      price: "On Demand",
      description:
        "Connect with vetted logistics partners in your area. Pay only when you use the service.",
      example:
        "Request connections to transport partners who specialize in scrap metal",
      icon: <FaUsers />,
    },
    {
      name: "Scrap Verification",
      price: "From as less as $50",
      description:
        "Not sure what your material is worth? Get expert assessment with market price guidance.",
      example:
        "Photo verification, material identification, current market pricing",
      icon: <FaChartLine />,
    },
  ];

  const testimonials = [
    {
      text: "I listed 5 tonnes of mixed metal on day one. Had 3 offers within 6 hours. This platform is what the industry needed.",
      author: "Marcus T.",
      location: "Perth Scrap Trader",
    },
    {
      text: "The verified badge and featured listings made buyers trust us immediately. We're closing deals faster than ever.",
      author: "Lisa R.",
      location: "Brisbane Recycling",
    },
    {
      text: "Finally, a platform that doesn't take a cut of our sales. Just pay for the tool, keep the profits.",
      author: "David K.",
      location: "Sydney Metal Yard",
    },
  ];

  const faqs = [
    {
      q: "What happens after my free trial period?",
      a: "Free plan is active for 30 days from sign-up (60 days for launch members). After that, you can upgrade to a paid plan to continue posting new listings, or your existing listings will remain active until they expire.",
    },

    {
      q: "Can I downgrade if Business is too much?",
      a: "Absolutely. Downgrade anytime. Your existing listings stay live, but new ones follow your plan limits.",
    },

    {
      q: "Do you take commission on sales?",
      a: "Yes, but only when you sell. You pay a small commission per successful transaction. We don't charge upfront fees on most plans - you only pay commission when you complete a sale.",
    },

    {
      q: "What's the launch offer discount?",
      a: "Join before April 10, 2026, get Pro features free initially (plus 60-day free trial instead of 30), then lock in up to 30% off for life when you convert to paid.",
    },

    {
      q: "What are 'surprise deals' for regular sellers?",
      a: "Active paid plan members who regularly list and sell on the platform unlock access to exclusive deals, premium buyer connections, and special marketplace opportunities not available to free users.",
    },

    {
      q: "Can I edit my listings after posting?",
      a: "Yes, all users can request listing edits. These are subject to admin approval to maintain quality and accuracy across the marketplace.",
    },

    {
      q: "Can I pay annually?",
      a: "Yes! Contact us for annual pricing options with additional savings on Business and Enterprise plans.",
    },

    {
      q: "What payment methods do you accept?",
      a: "Credit card, debit card, bank transfer (for annual plans), and invoice payment (Business and Enterprise plans).",
    },
  ];

  return (
    <div
      style={{
        background: "#ffffff",
        minHeight: "100vh",
        paddingBottom: theme.spacing(12),
      }}
    >
      {/* Hero Section with margin and radius */}
      <Container style={{ paddingTop: theme.spacing(8) }}>
        <section
          style={{
            background: `linear-gradient(135deg, ${hexToRgba(primary, 0.95)} 0%, ${hexToRgba(tertiary, 0.95)} 100%)`,
            padding: `${theme.spacing(12)} ${theme.spacing(6)}`,
            position: "relative",
            overflow: "hidden",
            borderRadius: "20px",
            marginBottom: theme.spacing(8),
          }}
        >
          {/* Decorative circles */}
          <div
            style={{
              position: "absolute",
              top: "-100px",
              right: "-100px",
              width: "400px",
              height: "400px",
              borderRadius: "50%",
              background: hexToRgba(secondary, 0.2),
              filter: "blur(80px)",
            }}
          />
          <div
            style={{
              position: "absolute",
              bottom: "-150px",
              left: "-150px",
              width: "500px",
              height: "500px",
              borderRadius: "50%",
              background: hexToRgba(success, 0.15),
              filter: "blur(100px)",
            }}
          />

          <div
            style={{
              position: "relative",
              zIndex: 1,
              textAlign: "center",
              color: bgPaper,
            }}
          >
            <h1
              style={{
                ...theme.typography.h1,
                fontSize: "clamp(2.5rem, 5vw, 4rem)",
                marginBottom: theme.spacing(4),
                color: bgPaper,
                textShadow: `0 4px 12px ${hexToRgba("#000", 0.1)}`,
              }}
            >
              Pricing Plans
            </h1>
            <p
              style={{
                ...theme.typography.h5,
                maxWidth: "700px",
                margin: "0 auto",
                color: hexToRgba(bgPaper, 0.95),
                fontWeight: 400,
              }}
            >
              Transparent pricing designed to grow with your scrap business
            </p>
          </div>
        </section>
      </Container>

      {/* Founding Member Banner - Below hero, changed background, shorter button */}
      <Container style={{ marginBottom: theme.spacing(12) }}>
        <Card
          style={{
            background: "#ffffff",
            border: `2px solid ${hexToRgba(primary, 0.3)}`,
            borderRadius: "16px",
            padding: theme.spacing(6),
            boxShadow: `0 8px 32px ${hexToRgba("#000", 0.08)}`,
            textAlign: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: theme.spacing(2),
              marginBottom: theme.spacing(3),
            }}
          >
            <FaGem size={32} color={tertiary} />
            <h2
              style={{ ...theme.typography.h2, color: textPrimary, margin: 0 }}
            >
              Early Bird Offer
            </h2>
          </div>
          <p
            style={{
              ...theme.typography.h5,
              color: textSecondary,
              marginBottom: theme.spacing(4),
              fontWeight: 400,
            }}
          >
            Join Now and secure exclusive benefits
          </p>

          <Row
            className="justify-content-center"
            style={{ marginBottom: theme.spacing(4) }}
          >
            <Col
              lg={2}
              md={4}
              sm={6}
              style={{ marginBottom: theme.spacing(2) }}
            >
              <div
                style={{
                  padding: theme.spacing(3),
                  background: hexToRgba(bgPaper, 0.6),
                  borderRadius: "12px",
                  border: `1px solid ${hexToRgba(primary, 0.2)}`,
                }}
              >
                <FaCheck
                  size={24}
                  color={success}
                  style={{ marginBottom: theme.spacing(2) }}
                />
                <p
                  style={{
                    color: textPrimary,
                    fontWeight: 600,
                    margin: 0,
                    fontSize: "0.9rem",
                  }}
                >
                  Pro features FREE until April 10, 2026
                </p>
              </div>
            </Col>
            <Col
              lg={2}
              md={4}
              sm={6}
              style={{ marginBottom: theme.spacing(2) }}
            >
              <div
                style={{
                  padding: theme.spacing(3),
                  background: hexToRgba(bgPaper, 0.6),
                  borderRadius: "12px",
                  border: `1px solid ${hexToRgba(primary, 0.2)}`,
                }}
              >
                <FaCheck
                  size={24}
                  color={success}
                  style={{ marginBottom: theme.spacing(2) }}
                />
                <p
                  style={{
                    color: textPrimary,
                    fontWeight: 600,
                    margin: 0,
                    fontSize: "0.9rem",
                  }}
                >
                  No Commission until April 10, 2026
                </p>
              </div>
            </Col>
            <Col
              lg={2}
              md={4}
              sm={6}
              style={{ marginBottom: theme.spacing(2) }}
            >
              <div
                style={{
                  padding: theme.spacing(3),
                  background: hexToRgba(bgPaper, 0.6),
                  borderRadius: "12px",
                  border: `1px solid ${hexToRgba(primary, 0.2)}`,
                }}
              >
                <FaCheck
                  size={24}
                  color={success}
                  style={{ marginBottom: theme.spacing(2) }}
                />
                <p
                  style={{
                    color: textPrimary,
                    fontWeight: 600,
                    margin: 0,
                    fontSize: "0.9rem",
                  }}
                >
                  Lock in up to 30% lifetime discount
                </p>
              </div>
            </Col>
            <Col
              lg={2}
              md={4}
              sm={6}
              style={{ marginBottom: theme.spacing(2) }}
            >
              <div
                style={{
                  padding: theme.spacing(3),
                  background: hexToRgba(bgPaper, 0.6),
                  borderRadius: "12px",
                  border: `1px solid ${hexToRgba(primary, 0.2)}`,
                }}
              >
                <FaCheck
                  size={24}
                  color={success}
                  style={{ marginBottom: theme.spacing(2) }}
                />
                <p
                  style={{
                    color: textPrimary,
                    fontWeight: 600,
                    margin: 0,
                    fontSize: "0.9rem",
                  }}
                >
                  Verified business badge ($39 value)
                </p>
              </div>
            </Col>
            <Col
              lg={2}
              md={4}
              sm={6}
              style={{ marginBottom: theme.spacing(2) }}
            >
              <div
                style={{
                  padding: theme.spacing(3),
                  background: hexToRgba(bgPaper, 0.6),
                  borderRadius: "12px",
                  border: `1px solid ${hexToRgba(primary, 0.2)}`,
                }}
              >
                <FaCheck
                  size={24}
                  color={success}
                  style={{ marginBottom: theme.spacing(2) }}
                />
                <p
                  style={{
                    color: textPrimary,
                    fontWeight: 600,
                    margin: 0,
                    fontSize: "0.9rem",
                  }}
                >
                  Early access to new features
                </p>
              </div>
            </Col>
          </Row>

          <div style={{ marginBottom: theme.spacing(4) }}>
            <p
              style={{
                ...theme.typography.body1,
                color: textPrimary,
                fontWeight: 600,
                marginBottom: theme.spacing(2),
              }}
            >
              127 of 500 founding members joined
            </p>
            <div
              style={{
                height: "12px",
                background: hexToRgba(tertiary, 0.2),
                borderRadius: "100px",
                overflow: "hidden",
                maxWidth: "500px",
                margin: "0 auto",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: "25.4%",
                  background: `linear-gradient(90deg, ${primary} 0%, ${tertiary} 100%)`,
                  borderRadius: "100px",
                  transition: "width 0.6s ease",
                }}
              />
            </div>
          </div>

          <p
            style={{
              ...theme.typography.h6,
              color: textPrimary,
              marginBottom: theme.spacing(4),
              fontWeight: 500,
            }}
          >
            Build your reputation now. Pay later. Keep the discount forever.
          </p>

          <div style={{ display: "flex", justifyContent: "center" }}>
            <Button
              size="sm"
              style={{
                background: `linear-gradient(135deg, ${primary} 0%, ${tertiary} 100%)`,
                color: bgPaper,
                border: "none",
                borderRadius: "8px",
                padding: `${theme.spacing(3)} ${theme.spacing(6)}`,
                fontWeight: 700,
                fontSize: "1rem",
                boxShadow: `0 4px 16px ${hexToRgba(primary, 0.3)}`,
                transition: `all ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut}`,
                width: "200px",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = `0 6px 20px ${hexToRgba(primary, 0.4)}`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = `0 4px 16px ${hexToRgba(primary, 0.3)}`;
              }}
              onClick={() => navigate("/register")}
            >
              Get Started Free
            </Button>
          </div>
        </Card>
      </Container>

      {/* Pricing Cards - Reduced radius, more glossy Most Popular */}
      <Container style={{ marginTop: theme.spacing(12) }}>
        <Row>
          {pricingPlans.map((plan, idx) => (
            <Col
              lg={3}
              md={6}
              key={plan.id}
              style={{ marginBottom: theme.spacing(4) }}
            >
              <Card
                style={{
                  height: "100%",
                  border: plan.highlight
                    ? `3px solid ${primary}`
                    : `1px solid ${hexToRgba(tertiary, 0.2)}`,
                  borderRadius: "12px",
                  background: plan.highlight
                    ? `linear-gradient(180deg, ${hexToRgba(primary, 0.08)} 0%, ${hexToRgba(bgPaper, 1)} 100%)`
                    : bgPaper,
                  boxShadow: plan.highlight
                    ? `0 20px 60px ${hexToRgba(primary, 0.35)}, inset 0 1px 0 ${hexToRgba("#fff", 0.5)}`
                    : `0 8px 24px ${hexToRgba("#000", 0.08)}`,
                  transform: plan.highlight ? "scale(1.05)" : "scale(1)",
                  transition: `all ${theme.transitions.duration.standard}ms ${theme.transitions.easing.easeInOut}`,
                  position: "relative",
                  overflow: "hidden",
                  backdropFilter: plan.highlight ? "blur(10px)" : "none",
                }}
                onMouseEnter={(e) => {
                  if (!plan.highlight) {
                    e.currentTarget.style.transform = "translateY(-8px)";
                    e.currentTarget.style.boxShadow = `0 16px 40px ${hexToRgba("#000", 0.12)}`;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!plan.highlight) {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = `0 8px 24px ${hexToRgba("#000", 0.08)}`;
                  }
                }}
              >
                {plan.highlight && (
                  <>
                    {/* Glossy overlay for Most Popular */}
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: `linear-gradient(180deg, ${hexToRgba("#fff", 0.15)} 0%, transparent 50%)`,
                        pointerEvents: "none",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        background: `linear-gradient(135deg, ${primary} 0%, ${tertiary} 100%)`,
                        color: bgPaper,
                        padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
                        borderBottomLeftRadius: "12px",
                        fontWeight: 700,
                        fontSize: "0.85rem",
                        boxShadow: `0 4px 12px ${hexToRgba("#000", 0.2)}`,
                      }}
                    >
                      {plan.badge}
                    </div>
                  </>
                )}

                <Card.Body
                  style={{ padding: theme.spacing(6), position: "relative" }}
                >
                  <div
                    style={{
                      textAlign: "center",
                      marginBottom: theme.spacing(4),
                    }}
                  >
                    <div
                      style={{
                        width: "70px",
                        height: "70px",
                        margin: "0 auto",
                        background: `linear-gradient(135deg, ${hexToRgba(primary, 0.15)} 0%, ${hexToRgba(secondary, 0.15)} 100%)`,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: theme.spacing(3),
                        color: textPrimary,
                        fontSize: "2rem",
                      }}
                    >
                      {plan.icon}
                    </div>
                    <h3
                      style={{
                        ...theme.typography.h4,
                        color: textPrimary,
                        marginBottom: theme.spacing(2),
                      }}
                    >
                      {plan.name}
                    </h3>
                    <div style={{ marginBottom: theme.spacing(2) }}>
                      <span
                        style={{
                          fontSize: "3rem",
                          fontWeight: 800,
                          color: textPrimary,
                        }}
                      >
                        {plan.price}
                      </span>
                      <span
                        style={{
                          fontSize: "1.1rem",
                          color: textSecondary,
                          fontWeight: 500,
                        }}
                      >
                        {plan.period}
                      </span>
                    </div>
                    <p
                      style={{
                        fontSize: "0.9rem",
                        color: textSecondary,
                        fontStyle: "italic",
                      }}
                    >
                      {plan.tagline}
                    </p>
                  </div>

                  <div
                    style={{
                      padding: theme.spacing(3),
                      background: hexToRgba(secondary, 0.2),
                      borderRadius: "8px",
                      marginBottom: theme.spacing(4),
                    }}
                  >
                    <p
                      style={{
                        fontSize: "0.85rem",
                        color: textSecondary,
                        fontWeight: 600,
                        margin: 0,
                      }}
                    >
                      <strong>Best for:</strong> {plan.bestFor}
                    </p>
                  </div>

                  {plan.launchSpecial && (
                    <div
                      style={{
                        padding: theme.spacing(3),
                        background: `linear-gradient(135deg, ${hexToRgba(success, 0.15)} 0%, ${hexToRgba(success, 0.05)} 100%)`,
                        borderRadius: "8px",
                        marginBottom: theme.spacing(4),
                        border: `2px dashed ${hexToRgba(success, 0.4)}`,
                      }}
                    >
                      <p
                        style={{
                          fontSize: "0.85rem",
                          color: textPrimary,
                          fontWeight: 600,
                          margin: 0,
                        }}
                      >
                        {plan.launchSpecial}
                      </p>
                    </div>
                  )}

                  <div style={{ marginBottom: theme.spacing(5) }}>
                    {plan.features.map((feature, i) => (
                      <div
                        key={i}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: theme.spacing(2),
                          marginBottom: theme.spacing(2),
                        }}
                      >
                        {feature.included ? (
                          <FaCheck
                            size={18}
                            color={success}
                            style={{ flexShrink: 0, marginTop: "2px" }}
                          />
                        ) : (
                          <FaTimes
                            size={18}
                            color={hexToRgba(textSecondary, 0.3)}
                            style={{ flexShrink: 0, marginTop: "2px" }}
                          />
                        )}
                        <span
                          style={{
                            fontSize: "0.9rem",
                            color: feature.included
                              ? textPrimary
                              : hexToRgba(textSecondary, 0.5),
                            fontWeight: feature.note ? 600 : 400,
                            fontStyle: feature.note ? "italic" : "normal",
                          }}
                        >
                          {feature.text}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* <div
                    style={{
                      padding: theme.spacing(3),
                      background: hexToRgba(secondary, 0.15),
                      borderRadius: "8px",
                      borderLeft: `4px solid ${primary}`,
                      marginBottom: theme.spacing(4),
                    }}
                  >
                    <p
                      style={{
                        fontSize: "0. nine rem",
                        color: textPrimary,
                        fontWeight: 600,
                        margin: 0,
                      }}
                    >
                     <strong>Commission:</strong> {plan.commission}
                    </p>
                  </div> */}
                  {/* <div
                    style={{
                      padding: theme.spacing(3),
                      background: hexToRgba(tertiary, 0.1),
                        borderRadius: "8px",
                        borderLeft: `4px solid ${tertiary}`,
                        marginBottom: theme.spacing(4),
                    }}
                   
                  >
                    <p
                      style={{
                        fontSize: "0. nine rem",
                        color: textSecondary,
                        margin: 0,
                      }}
                    >
                     <strong>Bonus:</strong>  {plan.bonus}
                    </p>
                  </div> */}

                  <Button
                    style={{
                      width: "100%",
                      background: plan.highlight
                        ? `linear-gradient(135deg, ${primary} 0%, ${tertiary} 100%)`
                        : `linear-gradient(135deg, ${hexToRgba(primary, 0.1)} 0%, ${hexToRgba(secondary, 0.1)} 100%)`,
                      color: plan.highlight ? bgPaper : textPrimary,
                      border: plan.highlight ? "none" : `2px solid ${primary}`,
                      borderRadius: "8px",
                      padding: theme.spacing(3),
                      fontWeight: 700,
                      fontSize: "1rem",
                      boxShadow: plan.highlight
                        ? `0 8px 24px ${hexToRgba(primary, 0.3)}`
                        : "none",
                      transition: `all ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut}`,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow = plan.highlight
                        ? `0 12px 32px ${hexToRgba(primary, 0.4)}`
                        : `0 8px 20px ${hexToRgba(primary, 0.15)}`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = plan.highlight
                        ? `0 8px 24px ${hexToRgba(primary, 0.3)}`
                        : "none";
                    }}
                    onClick={() => navigate("/register")}
                  >
                    {plan.cta}
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
        {/* <div style={{ textAlign: "center", marginTop: theme.spacing(4) }}>
          <p
            style={{
              ...theme.typography.body1,
              color: textSecondary,
              fontStyle: "italic",
              marginBottom: theme.spacing(2),
            }}
          >
            *Commission: Pay as you trade (from April 11, 2026). Bonus:
            Exclusive deals for active sellers (admin curated).
          </p>
        </div> */}
      </Container>

      {/*Important Information */}
      <Container style={{ marginTop: theme.spacing(16) }}>
        {/* Outer soft background */}
        <Card
          style={{
            background: "#ffffff",
            border: `2px solid ${hexToRgba(primary, 0.3)}`,
            borderRadius: "16px",
            padding: theme.spacing(6),
            boxShadow: `0 8px 32px ${hexToRgba("#000", 0.08)}`,
            textAlign: "center",
          }}
        >
          {/* Pill header */}
          <div style={{ textAlign: "center", marginBottom: theme.spacing(6) }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: theme.spacing(2),
                padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
                background: bgPaper,
                borderRadius: "999px",
                fontWeight: 700,
                letterSpacing: "0.05em",
                fontSize: "1.00rem",
                boxShadow: `0 6px 20px ${hexToRgba("#000", 0.08)}`,
              }}
            >
              <FaInfoCircle size={14} color={primary} />
              IMPORTANT INFORMATION
            </span>
          </div>

          {/* Info boxes */}
          <Row className="justify-content-center">
            {/* Box 1 */}
            <Col md={5} style={{ marginBottom: theme.spacing(4) }}>
              <div
                style={{
                  background: bgPaper,
                  borderRadius: "14px",
                  padding: theme.spacing(5),
                  height: "100%",
                  border: `1px solid ${hexToRgba(primary, 0.15)}`,
                  boxShadow: `0 10px 30px ${hexToRgba("#000", 0.06)}`,
                }}
              >
                {/* Title with icon */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: theme.spacing(3),
                    marginBottom: theme.spacing(2),
                  }}
                >
                  <FaDollarSign
                    size={20}
                    color={primary}
                    style={{ marginTop: 4 }}
                  />

                  <h5
                    style={{
                      color: textPrimary,
                      fontWeight: 700,
                      margin: 0,
                    }}
                  >
                    Commission: Pay as you trade
                  </h5>
                </div>

                <p
                  style={{
                    color: textSecondary,
                    fontSize: "0.95rem",
                    lineHeight: 1.6,
                    marginBottom: 0,
                  }}
                >
                  Starting April 11, 2026, a small commission applies per
                  successful transaction. Early bird members enjoy zero
                  commission until then.
                </p>
              </div>
            </Col>

            {/* Box 2 */}
            <Col md={5} style={{ marginBottom: theme.spacing(4) }}>
              <div
                style={{
                  background: bgPaper,
                  borderRadius: "14px",
                  padding: theme.spacing(5),
                  height: "100%",
                  border: `1px solid ${hexToRgba(primary, 0.15)}`,
                  boxShadow: `0 10px 30px ${hexToRgba("#000", 0.06)}`,
                }}
              >
                {/* Title with icon */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: theme.spacing(3),
                    marginBottom: theme.spacing(2),
                  }}
                >
                  <FaTag size={20} color={primary} style={{ marginTop: 4 }} />

                  <h5
                    style={{
                      color: textPrimary,
                      fontWeight: 700,
                      margin: 0,
                    }}
                  >
                    Bonus: Exclusive deals for active sellers
                  </h5>
                </div>

                <p
                  style={{
                    color: textSecondary,
                    fontSize: "0.95rem",
                    lineHeight: 1.6,
                    marginBottom: 0,
                  }}
                >
                  Our admin team curates special opportunities and premium buyer
                  connections for engaged platform members.
                </p>
              </div>
            </Col>
          </Row>
        </Card>
      </Container>

      {/* Add-ons Section */}
      <Container style={{ marginTop: theme.spacing(16) }}>
        <div style={{ textAlign: "center", marginBottom: theme.spacing(8) }}>
          <h2
            style={{
              ...theme.typography.h2,
              color: textPrimary,
              marginBottom: theme.spacing(3),
            }}
          >
            Boost Your Listings
          </h2>
          <p
            style={{
              ...theme.typography.h6,
              color: textSecondary,
              fontWeight: 400,
              maxWidth: "700px",
              margin: "0 auto",
            }}
          >
            Optional add-ons available on all paid plans
          </p>
        </div>

        <Row>
          {addons.map((addon, idx) => (
            <Col
              md={6}
              lg={4}
              key={idx}
              style={{ marginBottom: theme.spacing(4) }}
            >
              <Card
                style={{
                  height: "100%",
                  border: `1px solid ${hexToRgba(tertiary, 0.2)}`,
                  borderRadius: "12px",
                  background: bgPaper,
                  boxShadow: `0 4px 12px ${hexToRgba("#000", 0.06)}`,
                  transition: `all ${theme.transitions.duration.standard}ms ${theme.transitions.easing.easeInOut}`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-6px)";
                  e.currentTarget.style.boxShadow = `0 12px 28px ${hexToRgba("#000", 0.12)}`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = `0 4px 12px ${hexToRgba("#000", 0.06)}`;
                }}
              >
                <Card.Body style={{ padding: theme.spacing(5) }}>
                  <div
                    style={{
                      width: "60px",
                      height: "60px",
                      background: `linear-gradient(135deg, ${hexToRgba(primary, 0.15)} 0%, ${hexToRgba(secondary, 0.15)} 100%)`,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: theme.spacing(3),
                      color: textPrimary,
                      fontSize: "1.5rem",
                    }}
                  >
                    {addon.icon}
                  </div>
                  <h4
                    style={{
                      ...theme.typography.h5,
                      color: textPrimary,
                      marginBottom: theme.spacing(2),
                    }}
                  >
                    {addon.name}
                  </h4>
                  <p
                    style={{
                      fontSize: "1.2rem",
                      fontWeight: 700,
                      color: tertiary,
                      marginBottom: theme.spacing(3),
                    }}
                  >
                    {addon.price}
                  </p>
                  <p
                    style={{
                      fontSize: "0.95rem",
                      color: textSecondary,
                      marginBottom: theme.spacing(3),
                      lineHeight: 1.6,
                    }}
                  >
                    {addon.description}
                  </p>
                  <div
                    style={{
                      padding: theme.spacing(3),
                      background: hexToRgba(secondary, 0.15),
                      borderRadius: "8px",
                      borderLeft: `4px solid ${primary}`,
                    }}
                  >
                    <p
                      style={{
                        fontSize: "0.85rem",
                        color: textSecondary,
                        margin: 0,
                        fontStyle: "italic",
                      }}
                    >
                      {addon.example}
                    </p>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>

      {/* Comparison Table - Reduced radius, better readability, X icons instead of dashes */}
      <Container style={{ marginTop: theme.spacing(16) }}>
        <div style={{ textAlign: "center", marginBottom: theme.spacing(8) }}>
          <h2
            style={{
              ...theme.typography.h2,
              color: textPrimary,
              marginBottom: theme.spacing(3),
            }}
          >
            Compare All Plans
          </h2>
        </div>

        <Card
          style={{
            border: `1px solid ${hexToRgba(textSecondary, 0.2)}`,
            borderRadius: "12px",
            boxShadow: `0 12px 40px ${hexToRgba("#000", 0.1)}`,
            overflow: "hidden",
          }}
        >
          <div style={{ overflowX: "auto" }}>
            <Table hover responsive style={{ marginBottom: 0 }}>
              <thead
                style={{
                  background: `linear-gradient(135deg, ${hexToRgba(primary, 0.1)} 0%, ${hexToRgba(secondary, 0.1)} 100%)`,
                }}
              >
                <tr>
                  <th
                    style={{
                      padding: theme.spacing(4),
                      fontWeight: 700,
                      color: textPrimary,
                      fontSize: "1.05rem",
                    }}
                  >
                    Feature
                  </th>
                  <th
                    style={{
                      padding: theme.spacing(4),
                      fontWeight: 700,
                      color: textPrimary,
                      textAlign: "center",
                      fontSize: "1.05rem",
                    }}
                  >
                    Free
                  </th>
                  <th
                    style={{
                      padding: theme.spacing(4),
                      fontWeight: 700,
                      color: textPrimary,
                      textAlign: "center",
                      fontSize: "1.05rem",
                    }}
                  >
                    Basic
                  </th>
                  <th
                    style={{
                      padding: theme.spacing(4),
                      fontWeight: 700,
                      color: textPrimary,
                      textAlign: "center",
                      fontSize: "1.05rem",
                      background: hexToRgba(primary, 0.15),
                    }}
                  >
                    Pro
                  </th>
                  <th
                    style={{
                      padding: theme.spacing(4),
                      fontWeight: 700,
                      color: textPrimary,
                      textAlign: "center",
                      fontSize: "1.05rem",
                    }}
                  >
                    Business
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td
                    style={{
                      padding: theme.spacing(3),
                      fontWeight: 600,
                      color: textPrimary,
                      fontSize: "1rem",
                    }}
                  >
                    Active listings
                  </td>
                  <td
                    style={{
                      padding: theme.spacing(3),
                      textAlign: "center",
                      color: textSecondary,
                      fontSize: "1rem",
                    }}
                  >
                    3
                  </td>
                  <td
                    style={{
                      padding: theme.spacing(3),
                      textAlign: "center",
                      color: textSecondary,
                      fontSize: "1rem",
                    }}
                  >
                    20
                  </td>
                  <td
                    style={{
                      padding: theme.spacing(3),
                      textAlign: "center",
                      color: textPrimary,
                      background: hexToRgba(primary, 0.05),
                      fontWeight: 600,
                      fontSize: "1rem",
                    }}
                  >
                    Unlimited
                  </td>
                  <td
                    style={{
                      padding: theme.spacing(3),
                      textAlign: "center",
                      color: textPrimary,
                      fontWeight: 600,
                      fontSize: "1rem",
                    }}
                  >
                    Unlimited
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      padding: theme.spacing(3),
                      fontWeight: 600,
                      color: textPrimary,
                      fontSize: "1rem",
                    }}
                  >
                    Listing duration
                  </td>
                  <td
                    style={{
                      padding: theme.spacing(3),
                      textAlign: "center",
                      color: textSecondary,
                      fontSize: "1rem",
                    }}
                  >
                    30 days
                  </td>
                  <td
                    style={{
                      padding: theme.spacing(3),
                      textAlign: "center",
                      color: textSecondary,
                      fontSize: "1rem",
                    }}
                  >
                    60 days
                  </td>
                  <td
                    style={{
                      padding: theme.spacing(3),
                      textAlign: "center",
                      color: textPrimary,
                      background: hexToRgba(primary, 0.05),
                      fontWeight: 600,
                      fontSize: "1rem",
                    }}
                  >
                    90 days
                  </td>
                  <td
                    style={{
                      padding: theme.spacing(3),
                      textAlign: "center",
                      color: textPrimary,
                      fontWeight: 600,
                      fontSize: "1rem",
                    }}
                  >
                    90 days
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      padding: theme.spacing(3),
                      fontWeight: 600,
                      color: textPrimary,
                      fontSize: "1rem",
                    }}
                  >
                    Featured listings/month
                  </td>
                  <td
                    style={{
                      padding: theme.spacing(3),
                      textAlign: "center",
                      color: textSecondary,
                      fontSize: "1rem",
                    }}
                  >
                    <FaTimes size={16} color={hexToRgba(textSecondary, 0.4)} />
                  </td>
                  <td
                    style={{
                      padding: theme.spacing(3),
                      textAlign: "center",
                      color: textSecondary,
                      fontSize: "1rem",
                    }}
                  >
                    <FaTimes size={16} color={hexToRgba(textSecondary, 0.4)} />
                  </td>
                  <td
                    style={{
                      padding: theme.spacing(3),
                      textAlign: "center",
                      color: textPrimary,
                      background: hexToRgba(primary, 0.05),
                      fontWeight: 600,
                      fontSize: "1rem",
                    }}
                  >
                    3
                  </td>
                  <td
                    style={{
                      padding: theme.spacing(3),
                      textAlign: "center",
                      color: textPrimary,
                      fontWeight: 600,
                      fontSize: "1rem",
                    }}
                  >
                    10
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      padding: theme.spacing(3),
                      fontWeight: 600,
                      color: textPrimary,
                      fontSize: "1rem",
                    }}
                  >
                    Verified badge
                  </td>
                  <td
                    style={{
                      padding: theme.spacing(3),
                      textAlign: "center",
                      color: textSecondary,
                      fontSize: "1rem",
                    }}
                  >
                    <FaTimes size={16} color={hexToRgba(textSecondary, 0.4)} />
                  </td>
                  <td
                    style={{
                      padding: theme.spacing(3),
                      textAlign: "center",
                      color: textSecondary,
                      fontSize: "0.9rem",
                    }}
                  >
                    Add-on ($39)
                  </td>
                  <td
                    style={{
                      padding: theme.spacing(3),
                      textAlign: "center",
                      background: hexToRgba(primary, 0.05),
                    }}
                  >
                    <FaCheck size={18} color={success} />
                  </td>
                  <td
                    style={{ padding: theme.spacing(3), textAlign: "center" }}
                  >
                    <FaCheck size={18} color={success} />
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      padding: theme.spacing(3),
                      fontWeight: 600,
                      color: textPrimary,
                      fontSize: "1rem",
                    }}
                  >
                    Support
                  </td>
                  <td
                    style={{
                      padding: theme.spacing(3),
                      textAlign: "center",
                      color: textSecondary,
                      fontSize: "0.95rem",
                    }}
                  >
                    Email (24hr)
                  </td>
                  <td
                    style={{
                      padding: theme.spacing(3),
                      textAlign: "center",
                      color: textSecondary,
                      fontSize: "0.95rem",
                    }}
                  >
                    Email (24hr)
                  </td>
                  <td
                    style={{
                      padding: theme.spacing(3),
                      textAlign: "center",
                      color: textPrimary,
                      background: hexToRgba(primary, 0.05),
                      fontWeight: 600,
                      fontSize: "0.95rem",
                    }}
                  >
                    Priority (24hr)
                  </td>
                  <td
                    style={{
                      padding: theme.spacing(3),
                      textAlign: "center",
                      color: textPrimary,
                      fontWeight: 600,
                      fontSize: "0.95rem",
                    }}
                  >
                    Dedicated
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      padding: theme.spacing(3),
                      fontWeight: 600,
                      color: textPrimary,
                      fontSize: "1rem",
                    }}
                  >
                    Multi-user accounts
                  </td>
                  <td
                    style={{
                      padding: theme.spacing(3),
                      textAlign: "center",
                      color: textSecondary,
                    }}
                  >
                    <FaTimes size={16} color={hexToRgba(textSecondary, 0.4)} />
                  </td>
                  <td
                    style={{
                      padding: theme.spacing(3),
                      textAlign: "center",
                      color: textSecondary,
                    }}
                  >
                    <FaTimes size={16} color={hexToRgba(textSecondary, 0.4)} />
                  </td>
                  <td
                    style={{
                      padding: theme.spacing(3),
                      textAlign: "center",
                      color: textSecondary,
                      background: hexToRgba(primary, 0.05),
                    }}
                  >
                    <FaTimes size={16} color={hexToRgba(textSecondary, 0.4)} />
                  </td>
                  <td
                    style={{ padding: theme.spacing(3), textAlign: "center" }}
                  >
                    <FaCheck size={18} color={success} />
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      padding: theme.spacing(3),
                      fontWeight: 600,
                      color: textPrimary,
                      fontSize: "1rem",
                    }}
                  >
                    Trial period
                  </td>
                  <td
                    style={{
                      padding: theme.spacing(3),
                      textAlign: "center",
                      color: textSecondary,
                      fontSize: "0.85rem",
                    }}
                  >
                    30 days
                  </td>
                  <td
                    style={{
                      padding: theme.spacing(3),
                      textAlign: "center",
                      color: textSecondary,
                      fontSize: "0.95rem",
                    }}
                  >
                    April 10, 2026
                  </td>
                  <td
                    style={{
                      padding: theme.spacing(3),
                      textAlign: "center",
                      color: textPrimary,
                      background: hexToRgba(primary, 0.05),
                      fontWeight: 600,
                      fontSize: "0.9rem",
                    }}
                  >
                    April 10, 2026
                  </td>
                  <td
                    style={{
                      padding: theme.spacing(3),
                      textAlign: "center",
                      color: textPrimary,
                      fontWeight: 600,
                      fontSize: "0.95rem",
                    }}
                  >
                    April 10, 2026
                  </td>
                </tr>
                <tr style={{ background: hexToRgba(primary, 0.08) }}>
                  <td
                    style={{
                      padding: theme.spacing(4),
                      fontWeight: 700,
                      color: textPrimary,
                      fontSize: "1.15rem",
                    }}
                  >
                    Price
                  </td>
                  <td
                    style={{
                      padding: theme.spacing(4),
                      textAlign: "center",
                      fontWeight: 700,
                      color: textPrimary,
                      fontSize: "1.15rem",
                    }}
                  >
                    $0
                  </td>
                  <td
                    style={{
                      padding: theme.spacing(4),
                      textAlign: "center",
                      fontWeight: 700,
                      color: textPrimary,
                      fontSize: "1.15rem",
                    }}
                  >
                    $29.95/mo
                  </td>
                  <td
                    style={{
                      padding: theme.spacing(4),
                      textAlign: "center",
                      fontWeight: 700,
                      color: textPrimary,
                      fontSize: "1.15rem",
                      background: hexToRgba(primary, 0.2),
                    }}
                  >
                    $59.95/mo
                  </td>
                  <td
                    style={{
                      padding: theme.spacing(4),
                      textAlign: "center",
                      fontWeight: 700,
                      color: textPrimary,
                      fontSize: "1.15rem",
                    }}
                  >
                    $149.95/mo
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>
        </Card>
      </Container>

      {/* Testimonials */}
      <Container style={{ marginTop: theme.spacing(16) }}>
        <div style={{ textAlign: "center", marginBottom: theme.spacing(8) }}>
          <h2
            style={{
              ...theme.typography.h2,
              color: textPrimary,
              marginBottom: theme.spacing(3),
            }}
          >
            What Our Early Users Say
          </h2>
        </div>

        <Row>
          {testimonials.map((testimonial, idx) => (
            <Col md={4} key={idx} style={{ marginBottom: theme.spacing(4) }}>
              <Card
                style={{
                  height: "100%",
                  border: "none",
                  borderRadius: "12px",
                  background: `linear-gradient(135deg, ${hexToRgba(bgPaper, 0.9)} 0%, ${hexToRgba(secondary, 0.05)} 100%)`,
                  boxShadow: `0 8px 24px ${hexToRgba("#000", 0.08)}`,
                  transition: `all ${theme.transitions.duration.standard}ms ${theme.transitions.easing.easeInOut}`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-6px)";
                  e.currentTarget.style.boxShadow = `0 16px 40px ${hexToRgba("#000", 0.12)}`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = `0 8px 24px ${hexToRgba("#000", 0.08)}`;
                }}
              >
                <Card.Body style={{ padding: theme.spacing(5) }}>
                  <FaQuoteLeft
                    size={32}
                    color={hexToRgba(primary, 0.4)}
                    style={{ marginBottom: theme.spacing(3) }}
                  />
                  <p
                    style={{
                      ...theme.typography.body1,
                      color: textPrimary,
                      marginBottom: theme.spacing(4),
                      lineHeight: 1.7,
                      fontStyle: "italic",
                    }}
                  >
                    "{testimonial.text}"
                  </p>
                  <div>
                    <p
                      style={{
                        fontWeight: 700,
                        color: textPrimary,
                        marginBottom: theme.spacing(1),
                      }}
                    >
                      {testimonial.author}
                    </p>
                    <p
                      style={{
                        fontSize: "0.9rem",
                        color: textSecondary,
                        margin: 0,
                      }}
                    >
                      {testimonial.location}
                    </p>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>

      {/* FAQs - Replaced with Accordion */}
      <Container style={{ marginTop: theme.spacing(16) }}>
        <div style={{ textAlign: "center", marginBottom: theme.spacing(8) }}>
          <h2
            style={{
              ...theme.typography.h2,
              color: textPrimary,
              marginBottom: theme.spacing(3),
            }}
          >
            Common Questions
          </h2>
        </div>

        <Row className="justify-content-center">
          <Col lg={10}>
            <Accordion defaultActiveKey="0">
              {faqs.map((faq, idx) => (
                <Accordion.Item
                  key={idx}
                  eventKey={idx.toString()}
                  style={{
                    border: "none",
                    marginBottom: theme.spacing(3),
                    borderRadius: "12px",
                    overflow: "hidden",
                    boxShadow: `0 4px 12px ${hexToRgba("#000", 0.06)}`,
                  }}
                >
                  <Accordion.Header
                    style={{
                      background: bgPaper,
                      borderRadius: "12px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: theme.spacing(2),
                        width: "100%",
                      }}
                    >
                      <span
                        style={{
                          ...theme.typography.h6,
                          color: textPrimary,
                          fontWeight: 700,
                          flex: 1,
                        }}
                      >
                        {faq.q}
                      </span>
                    </div>
                  </Accordion.Header>
                  <Accordion.Body
                    style={{
                      background: hexToRgba(secondary, 0.05),
                      padding: theme.spacing(5),
                    }}
                  >
                    <p
                      style={{
                        ...theme.typography.body1,
                        color: textSecondary,
                        margin: 0,
                        lineHeight: 1.7,
                      }}
                    >
                      {faq.a}
                    </p>
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          </Col>
        </Row>
      </Container>

      {/* CTA Section */}
      <Container style={{ marginTop: theme.spacing(16) }}>
        <Card
          style={{
            border: "none",
            borderRadius: "20px",
            background: `linear-gradient(135deg, ${hexToRgba(primary, 0.95)} 0%, ${hexToRgba(tertiary, 0.95)} 100%)`,
            boxShadow: `0 20px 60px ${hexToRgba("#000", 0.15)}`,
            padding: theme.spacing(12),
            textAlign: "center",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Decorative elements */}
          <div
            style={{
              position: "absolute",
              top: "-50px",
              right: "-50px",
              width: "300px",
              height: "300px",
              borderRadius: "50%",
              background: hexToRgba(secondary, 0.2),
              filter: "blur(60px)",
            }}
          />
          <div
            style={{
              position: "absolute",
              bottom: "-80px",
              left: "-80px",
              width: "350px",
              height: "350px",
              borderRadius: "50%",
              background: hexToRgba(success, 0.15),
              filter: "blur(70px)",
            }}
          />

          <div style={{ position: "relative", zIndex: 1 }}>
            <h2
              style={{
                ...theme.typography.h2,
                color: bgPaper,
                marginBottom: theme.spacing(3),
              }}
            >
              Ready to Start?
            </h2>
            <p
              style={{
                ...theme.typography.h5,
                color: hexToRgba(bgPaper, 0.95),
                marginBottom: theme.spacing(6),
                fontWeight: 400,
                maxWidth: "700px",
                margin: `0 auto ${theme.spacing(6)} auto`,
              }}
            >
              Join 127 early birds members who are already building their
              reputation on ScrapTrade
            </p>

            <div
              style={{
                display: "flex",
                gap: theme.spacing(3),
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Button
                size="lg"
                style={{
                  background: bgPaper,
                  color: textPrimary,
                  border: "none",
                  borderRadius: "8px",
                  padding: `${theme.spacing(3)} ${theme.spacing(8)}`,
                  fontWeight: 700,
                  fontSize: "1.1rem",
                  boxShadow: `0 8px 24px ${hexToRgba("#000", 0.2)}`,
                  transition: `all ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut}`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-3px)";
                  e.currentTarget.style.boxShadow = `0 12px 32px ${hexToRgba("#000", 0.25)}`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = `0 8px 24px ${hexToRgba("#000", 0.2)}`;
                }}
                onClick={() => navigate("/register")}
              >
                Create Free Account
              </Button>

              <Button
                size="lg"
                variant="outline"
                style={{
                  background: "transparent",
                  color: bgPaper,
                  border: `2px solid ${bgPaper}`,
                  borderRadius: "8px",
                  padding: `${theme.spacing(3)} ${theme.spacing(8)}`,
                  fontWeight: 700,
                  fontSize: "1.1rem",
                  transition: `all ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut}`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = bgPaper;
                  e.currentTarget.style.color = textPrimary;
                  e.currentTarget.style.transform = "translateY(-3px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = bgPaper;
                  e.currentTarget.style.transform = "translateY(0)";
                }}
                onClick={() => navigate("/register")}
              >
                See a Demo
              </Button>

              <Button
                size="lg"
                variant="outline"
                style={{
                  background: "transparent",
                  color: bgPaper,
                  border: `2px solid ${bgPaper}`,
                  borderRadius: "8px",
                  padding: `${theme.spacing(3)} ${theme.spacing(8)}`,
                  fontWeight: 700,
                  fontSize: "1.1rem",
                  transition: `all ${theme.transitions.duration.short}ms ${theme.transitions.easeInOut}`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = bgPaper;
                  e.currentTarget.style.color = textPrimary;
                  e.currentTarget.style.transform = "translateY(-3px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = bgPaper;
                  e.currentTarget.style.transform = "translateY(0)";
                }}
                onClick={() => navigate("/contact-us")}
              >
                Talk to Sales
              </Button>
            </div>
          </div>
        </Card>
      </Container>
    </div>
  );
};

export default PricingPage;
